import { z } from 'zod';
import { isoDateRegexString } from '@erikdakoda/i18n/shared/date';
import {
  createAiToolFieldMaskSchema,
  type AiToolFieldMaskExpansionConfig,
} from '@erikdakoda/ai-tool/findManyAiToolInput';
import { APP_IDS, getCurrentAppId } from '@erikdakoda/utils/getCurrentAppId';
import {
  getVocabularyValues,
  VOCABULARY_APP_SLOT_MAPS,
} from '@erikdakoda/database/shared/vocabularyRegistry';
import { seedUviloVocabulary } from '../shared/seedUviloVocabulary';

/** Scalar fields selectable on vectorSearchFactoids results. */
export const FACTOID_VECTOR_SEARCH_FIELDS = [
  'id',
  'app',
  'category1',
  'category2',
  'text',
  'date',
] as const;

/** Default field mask for vectorSearchFactoids `select`. */
export const factoidForVectorSearchFieldMaskConfig: AiToolFieldMaskExpansionConfig = {
  defaultSelect: Object.fromEntries(FACTOID_VECTOR_SEARCH_FIELDS.map((field) => [field, true])),
  scalarFields: FACTOID_VECTOR_SEARCH_FIELDS,
};

function vocabularyEnum(values: readonly string[]) {
  return z.enum(values as [string, ...string[]]);
}

function formatValues(values: readonly string[]): string {
  return values.join(', ');
}

function category1Schema(appId: string, namespace: string | undefined, values: readonly string[]) {
  if (namespace !== undefined && values.length > 0) {
    return vocabularyEnum(values).describe(
      `What kind of fact this is. Values from the ${appId} ${namespace} vocabulary: ${formatValues(values)}`,
    );
  }
  return z.string().describe('What kind of fact this is');
}

function category2Schema(appId: string, namespace: string | undefined, values: readonly string[]) {
  if (namespace !== undefined && values.length > 0) {
    return vocabularyEnum(values).describe(
      `Which area the fact concerns. Values from the ${appId} ${namespace} vocabulary: ${formatValues(values)}`,
    );
  }
  return z.string().describe('Which area the fact concerns');
}

function describeCategory1Filter(
  appId: string,
  namespace: string | undefined,
  values: readonly string[],
): string {
  if (namespace !== undefined && values.length > 0) {
    return `Optional filter by category1 (what kind of fact). Values from the ${appId} ${namespace} vocabulary: ${formatValues(values)}. Empty or undefined will match all values; multiple values use the OR operator`;
  }
  return 'Optional filter by category1 (what kind of fact). Empty or undefined will match all values; multiple values use the OR operator';
}

function describeCategory2Filter(
  appId: string,
  namespace: string | undefined,
  values: readonly string[],
): string {
  if (namespace !== undefined && values.length > 0) {
    return `Optional filter by category2 (which area the fact concerns). Values from the ${appId} ${namespace} vocabulary: ${formatValues(values)}. Empty or undefined will match all values; multiple values use the OR operator`;
  }
  return 'Optional filter by category2 (which area the fact concerns). Empty or undefined will match all values; multiple values use the OR operator';
}

function describeToolVocabularyFilters(
  appId: string,
  category1Values: readonly string[],
  category2Values: readonly string[],
): string {
  if (category1Values.length > 0 && category2Values.length > 0) {
    return `category1 (${formatValues(category1Values)}) and/or category2 (${formatValues(category2Values)}). Filters use the ${appId} app vocabulary`;
  }
  return `category1 and/or category2. Filters use the ${appId} app vocabulary when values are registered`;
}

function category1FilterInputSchema(
  appId: string,
  namespace: string | undefined,
  values: readonly string[],
) {
  const description = describeCategory1Filter(appId, namespace, values);
  if (values.length > 0) {
    return z.array(vocabularyEnum(values)).optional().describe(description);
  }
  return z.array(z.string()).optional().describe(description);
}

function category2FilterInputSchema(
  appId: string,
  namespace: string | undefined,
  values: readonly string[],
) {
  const description = describeCategory2Filter(appId, namespace, values);
  if (values.length > 0) {
    return z.array(vocabularyEnum(values)).optional().describe(description);
  }
  return z.array(z.string()).optional().describe(description);
}

function buildFactoidForAiToolsSchema(
  appId: string,
  category1Namespace: string | undefined,
  category2Namespace: string | undefined,
  category1Values: readonly string[],
  category2Values: readonly string[],
) {
  return z.object({
    id: z.string().describe('Unique identifier for the factoid'),
    app: z.enum(APP_IDS).describe('App namespace whose vocabulary the categories come from'),
    category1: category1Schema(appId, category1Namespace, category1Values),
    category2: category2Schema(appId, category2Namespace, category2Values),
    text: z.string().describe('The factoid text content'),
    date: z
      .string()
      .regex(new RegExp(isoDateRegexString))
      .nullable()
      .optional()
      .describe('Optional date associated with the factoid in YYYY-MM-DD format'),
    similarity: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .describe('Cosine similarity score for vector search results'),
  });
}

function buildListFactoidsInputSchema(
  appId: string,
  category1Namespace: string | undefined,
  category2Namespace: string | undefined,
  category1Values: readonly string[],
  category2Values: readonly string[],
) {
  return z.object({
    category1: category1FilterInputSchema(appId, category1Namespace, category1Values),
    category2: category2FilterInputSchema(appId, category2Namespace, category2Values),
    skip: z
      .number()
      .int()
      .min(0)
      .default(0)
      .describe('Number of results to skip for pagination (default: 0)'),
    take: z
      .number()
      .int()
      .min(1)
      .max(100)
      .default(20)
      .describe('Maximum number of results to return (default: 20, max: 100)'),
  });
}

function buildVectorSearchFactoidsInputSchema(
  appId: string,
  category1Namespace: string | undefined,
  category2Namespace: string | undefined,
  category1Values: readonly string[],
  category2Values: readonly string[],
) {
  return z.object({
    searchText: z
      .string()
      .min(1)
      .max(500)
      .describe('Text to search for in factoids using semantic/vector similarity'),
    category1: category1FilterInputSchema(appId, category1Namespace, category1Values),
    category2: category2FilterInputSchema(appId, category2Namespace, category2Values),
    limit: z
      .number()
      .int()
      .min(1)
      .max(50)
      .default(5)
      .describe('Maximum number of results to return (default: 5, max: 50)'),
    minSimilarity: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .describe(
        'Optional minimum cosine similarity score (0-1) to include in results; lower values return more results but may be less relevant (default: 0.28)',
      ),
    select: createAiToolFieldMaskSchema(FACTOID_VECTOR_SEARCH_FIELDS),
  });
}

function buildListFactoidsToolDescription(
  appId: string,
  category1Values: readonly string[],
  category2Values: readonly string[],
): string {
  return `List factoids (remembered facts about the user) with optional filtering and pagination.

- Can filter by ${describeToolVocabularyFilters(appId, category1Values, category2Values)}.
- Supports pagination with \`skip\` and \`take\` parameters.
- Returns results in reverse chronological order.`;
}

function buildVectorSearchFactoidsToolDescription(
  appId: string,
  category1Values: readonly string[],
  category2Values: readonly string[],
): string {
  return `Search for factoids (remembered facts about the user) using semantic/vector similarity search.

- Provide natural language text to search for conceptually similar factoids.
- Can optionally filter by ${describeToolVocabularyFilters(appId, category1Values, category2Values)}.
- Returns results ordered by relevance with cosine similarity scores.`;
}

function buildListFactoidsOutputSchema(
  factoidSchema: ReturnType<typeof buildFactoidForAiToolsSchema>,
) {
  return z.object({
    results: z.array(factoidSchema.partial()).describe('Array of matching factoid items'),
    count: z.number().int().describe('Total number of results found'),
    success: z
      .string()
      .nullable()
      .optional()
      .describe('Success message when listing completes successfully'),
    error: z.unknown().nullable().optional().describe('Error message or object when listing fails'),
  });
}

function buildVectorSearchFactoidsOutputSchema(
  factoidSchema: ReturnType<typeof buildFactoidForAiToolsSchema>,
) {
  return z.object({
    results: z.array(factoidSchema.partial()).describe('Array of matching factoid items'),
    count: z.number().int().describe('Total number of results found'),
    success: z
      .string()
      .nullable()
      .optional()
      .describe('Success message when search completes successfully'),
    error: z.unknown().nullable().optional().describe('Error message or object when search fails'),
  });
}

/**
 * Factoid AI-tool schemas and descriptions built from one vocabulary registry snapshot.
 */
export type FactoidToolSchemas = {
  readonly factoid: ReturnType<typeof buildFactoidForAiToolsSchema>;
  readonly listInput: ReturnType<typeof buildListFactoidsInputSchema>;
  readonly listOutput: ReturnType<typeof buildListFactoidsOutputSchema>;
  readonly vectorInput: ReturnType<typeof buildVectorSearchFactoidsInputSchema>;
  readonly vectorOutput: ReturnType<typeof buildVectorSearchFactoidsOutputSchema>;
  readonly listDescription: string;
  readonly vectorDescription: string;
};

let cached: FactoidToolSchemas | undefined;

/**
 * Builds factoid tool schemas from the running app's vocabulary slot map.
 * Seeds uvilo vocabulary when both slots are still empty (safe before initPackages).
 * Memoizes only when both category slots have registered values so a call before
 * seeding does not pin empty schemas.
 * @returns Schemas and tool description strings sharing one category1/category2 snapshot
 */
export function getFactoidToolSchemas(): FactoidToolSchemas {
  if (cached) {
    return cached;
  }

  const appId = getCurrentAppId();
  const slotMap = VOCABULARY_APP_SLOT_MAPS[appId];
  const category1Namespace = slotMap?.category1;
  const category2Namespace = slotMap?.category2;
  let category1Values =
    category1Namespace !== undefined ? getVocabularyValues(appId, category1Namespace) : [];
  let category2Values =
    category2Namespace !== undefined ? getVocabularyValues(appId, category2Namespace) : [];

  if (
    slotMap &&
    category1Namespace !== undefined &&
    category2Namespace !== undefined &&
    category1Values.length === 0 &&
    category2Values.length === 0
  ) {
    seedUviloVocabulary();
    category1Values = getVocabularyValues(appId, category1Namespace);
    category2Values = getVocabularyValues(appId, category2Namespace);
  }

  const hasValues = category1Values.length > 0 && category2Values.length > 0;

  const factoid = buildFactoidForAiToolsSchema(
    appId,
    category1Namespace,
    category2Namespace,
    category1Values,
    category2Values,
  );

  const snapshot: FactoidToolSchemas = {
    factoid,
    listInput: buildListFactoidsInputSchema(
      appId,
      category1Namespace,
      category2Namespace,
      category1Values,
      category2Values,
    ),
    listOutput: buildListFactoidsOutputSchema(factoid),
    vectorInput: buildVectorSearchFactoidsInputSchema(
      appId,
      category1Namespace,
      category2Namespace,
      category1Values,
      category2Values,
    ),
    vectorOutput: buildVectorSearchFactoidsOutputSchema(factoid),
    listDescription: buildListFactoidsToolDescription(appId, category1Values, category2Values),
    vectorDescription: buildVectorSearchFactoidsToolDescription(
      appId,
      category1Values,
      category2Values,
    ),
  };

  if (hasValues) {
    cached = snapshot;
  }

  return snapshot;
}

/** Factoid shape returned by list/search AI tools. */
export type FactoidForAiTools = z.infer<FactoidToolSchemas['factoid']>;

/** Input for listFactoids. */
export type ListFactoidsInput = z.infer<FactoidToolSchemas['listInput']>;

/** Output for listFactoids. */
export type ListFactoidsOutput = z.infer<FactoidToolSchemas['listOutput']>;

/** Input for vectorSearchFactoids. */
export type VectorSearchFactoidsInput = z.infer<FactoidToolSchemas['vectorInput']>;

/** Output for vectorSearchFactoids. */
export type VectorSearchFactoidsOutput = z.infer<FactoidToolSchemas['vectorOutput']>;
